import { BaseCache, makeDeviceCache } from "./storage/cache";
import { UserService } from "./users";
import { getCurrentVehicle, isNoVehicle } from "./device";
import { DriveStore } from "../store/reducers";
import { extend } from "./vanilla";
import { Device } from "../model/Device";
import { IFeatureFlagsService } from "../services/featureFlags";
import { FeatureFlagName } from "./featureFlagName";

interface ISecurityGroupHash {
    [securityId: string]: ISecurityGroup;
}

interface ISecurityIdentifierHash {
    [securityId: string]: ISecurityIdentifier;
}

type TSecurityDefinition = "useDrive" | "hasHosClearance" | "attemptHos" | "useHos" | "canIgnoreHOSLogs" | "editPcYmLogs" | "userRecordHosData"
    | "viewDvir" | "useDvir" | "repairDvir" | "useMessaging" | "getMessages" | "sendMessages" | "getRouteMessages" | "getNews"
    | "dismissNews" | "viewShipments" | "editShipments" | "viewTrailers" | "editTrailers" | "changePassword" | "editUser" | "getUsers" | "viewDriverClockInOut"
    | "inspectDvir" | "viewMedia" | "manageMedia" | "certifyDvir" | "showTrips" | "manageTripAnnotation" | "viewTripAnnotation" | "manageGroupDevice" | "useDriveWithoutExceptionsReport"
    | "viewDriverSafety" | "emailLogs"
    | "viewAssetInspectionTemplates" | "manageAssetInspectionTemplates" | "viewAssetInspectionLogs" | "manageAssetInspectionLogs"
    | "useAssetInspection" | "inspectAssetInspection" | "repairAssetInspection" | "certifyAssetInspection" | "viewInspectionLocation" | "viewCoachingSession" | "manageCoachingSession";

const FEATURE_FLAG_PREFIX = "FeatureFlag:";

/**
 * Security definitions for the application
 * The function result is an array of strings that represent the security identifiers required to access the feature
 * First level of the arrays are OR'd together, second level are AND'd together.
 * E.g. ["DeviceList", ["ExceptionsReport", "KeyInformation"]] means the user needs DeviceList AND either ExceptionsReport OR KeyInformation
 */
const DEFS: { [securityDefinition in TSecurityDefinition]: Function } = {
    useDrive: () => ["DeviceList", ["ExceptionsReport", "KeyInformation"]],
    canIgnoreHOSLogs: () => ["IgnoreHOSLogs"],
    hasHosClearance: () => ["ManageHOSLogs", "ViewHOSLogs"],
    editPcYmLogs: () => ["HosLogEditPcYm"],
    attemptHos: function () {
        return this.hasHosClearance().concat(this.userRecordHosData());
    },
    useHos: function () {
        let attempt = this.attemptHos();
        attempt.push("Device:UseHos");
        return attempt;
    },
    viewDriverClockInOut: () => ["ViewDriverClockInOut"],
    userRecordHosData: () => ["User:IsDriver"],
    viewDvir: () => ["ViewDVIRLogs"],
    useDvir: () => ["ManageDVIRLogs", "ViewDVIRLogs"],
    repairDvir: () => ["RepairDVIR"],
    certifyDvir: () => ["CertifyDVIR"],
    inspectDvir: () => ["InspectDVIR"],
    viewMedia: () => ["ViewMedia"],
    manageMedia: () => ["ManageMedia"],
    useMessaging: function () {
        return [this.getMessages(), this.sendMessages()];
    },
    getMessages: () => ["ViewGpsTextMessage"],
    sendMessages: () => ["SendGpsTextMessage"],
    getRouteMessages: () => ["RouteList"],
    getNews: () => [["NotificationList"], ["ViewGpsTextMessage"]],
    dismissNews: () => [["NotificationList"], ["ViewGpsTextMessage"]],
    viewShipments: () => ["ViewShipments"],
    editShipments: () => ["ManageShipments"],
    viewTrailers: () => ["ViewTrailers"],
    editTrailers: () => ["ManageTrailers"],

    // TODO: requires permission migration — Drive app has no RBAC switch infrastructure; ChangePassword/UserSettings/UserList are Users & Drivers-owned
    changePassword: () => ["ChangePassword"],
    editUser: () => ["UserSettings"],

    // Users that do not have UserList might have access to ViewDVIRLogs, or ViewHOSLogs, or ViewGpsTextMessage all of which grant API access to retrieve Users
    getUsers: () => [["UserList"], ["ViewDVIRLogs"], ["ViewHOSLogs"], ["ViewGpsTextMessage"]],
    showTrips: () => ["TripsActivityReport"],

    // TODO: requires permission migration — Feature:TripAnnotations @ FeatureAddOrDelete once Drive gains RBAC infra
    manageTripAnnotation: () => ["ManageTripAnnotation"],
    // TODO: requires permission migration — Feature:TripAnnotations @ FeatureView once Drive gains RBAC infra
    viewTripAnnotation: () => ["ViewTripAnnotation"],
    manageGroupDevice: () => ["ManageDriverActivityGroup"],

    // MYG-33102: Log if missing KeyInformation clearance
    useDriveWithoutExceptionsReport: () => ["DeviceList", "KeyInformation"],

    viewDriverSafety: () => ["ViewDriverSafety"],
    // TODO: requires permission migration — HOS-owned per the mapping doc; Drive app has no RBAC switch infrastructure
    emailLogs: () => ["EmailLogsToCustomRecipients"],

    // Driver Coaching security clearances
    viewCoachingSession: () => ["ViewCoachingSession"],
    manageCoachingSession: () => ["ManageCoachingSession"],

    // Asset Inspection security clearances
    useAssetInspection: () => [["ViewAssetInspectionLogs"], ["InspectAssetInspection"]],
    viewAssetInspectionTemplates: () => ["ViewAssetInspectionTemplates"],
    manageAssetInspectionTemplates: () => ["ManageAssetInspectionTemplates"],
    viewAssetInspectionLogs: () => ["ViewAssetInspectionLogs"],
    manageAssetInspectionLogs: () => ["ManageAssetInspectionLogs", "ViewAssetInspectionLogs"],
    inspectAssetInspection: () => ["InspectAssetInspection"],
    // ManageAssetInspectionLogs implies repair (MYG-149567); keep in sync with the server's InspectedItemBridge and DriveNative's mapIdentifiersToPermissions.
    repairAssetInspection: () => [["RepairAssetInspection"], ["ManageAssetInspectionLogs"]],
    certifyAssetInspection: () => ["CertifyAssetInspection"],
    viewInspectionLocation: () => ["ViewInspectionLocation"]
};

const PAGE_SECURITY_DEFS = {
    "hos": DEFS.useHos(),
    "dvir": DEFS.useDvir(),
    "messaging": DEFS.useMessaging(),
    "assets/manageTrailers": DEFS.viewTrailers(),
    "assets/addTrailer": DEFS.editTrailers(),
    "assets/addShipment": DEFS.editShipments(),
    "settings/passwordChange": DEFS.changePassword(),
    "settings/timezoneChange": DEFS.editUser(),
    "settings/languageChange": DEFS.editUser(),
    "settings/homeTerminalChange": DEFS.editUser(),
    "driverSafety": DEFS.viewDriverSafety(),
    "coaching": [...DEFS.viewCoachingSession(), ...DEFS.manageCoachingSession()],
    "ui/emailLogs/main": DEFS.emailLogs(),
    "modules/inspections": ([] as string[]).concat(...DEFS.useAssetInspection())
};

export class SecurityService {
    private readonly deviceCache: BaseCache<Device | IDevice>;

    constructor(private readonly store: DriveStore, private readonly userService: UserService, private readonly featureFlagService: IFeatureFlagsService) {
        this.deviceCache = makeDeviceCache(store);

        // Noop usage of service to bypass TS compile. Foresee service being used here in the future 🔮
        this.featureFlagService.hasFeatureFlag("NOOP" as FeatureFlagName);
    }

    calculatePermissions(securityClearances: ISecurityGroup[] = [], userClearances: DeepReadonly<ISecurityGroup[]> = []): string[] {
        let securityDict: ISecurityGroupHash = {};
        let parentRelDict: ISecurityGroupHash = {};
        let userFilters: ISecurityIdentifierHash = {};
        let walkUserClearanceTopDownRecursive = (filters: ISecurityIdentifierHash, clearance: DeepReadonly<ISecurityGroup>) => {
            let clearanceId = clearance.id;
            let parent = parentRelDict[clearanceId];

            // Populate security clearance
            let populatedClearance = securityDict[clearance.id];

            if (parent) {
                walkUserClearanceTopDownRecursive(filters, parentRelDict[clearanceId]);
            }

            if (populatedClearance) {
                let currentFilters = populatedClearance.securityFilters && populatedClearance.securityFilters.length
                    ? populatedClearance.securityFilters
                    : [ ...(parent.securityFilters || []) ];

                currentFilters.forEach(currentFilter => {
                    let securityIdentifierId = this.getSecurityIdentifierId(currentFilter);

                    if (securityIdentifierId === "Everything") {
                        for (let id in filters) {
                            if (filters.hasOwnProperty(id)) {
                                filters[id].isAdd = currentFilter.isAdd;
                            }
                        }
                        return;
                    }

                    filters[securityIdentifierId] = currentFilter;
                });
            }
        };

        securityClearances.forEach(clearance => {
            securityDict[clearance.id] = clearance;

            if (clearance.children && clearance.children.length) {
                clearance.children.forEach(child => {
                    parentRelDict[child.id] = clearance;
                });
            }
        });

        userClearances.forEach(clearance => {
            walkUserClearanceTopDownRecursive(userFilters, clearance);
        });

        return Object.keys(userFilters)
            .map(key => userFilters[key])
            .filter(f => f.isAdd)
            .map(f => this.getSecurityIdentifierId(f));
    }

    canNavigate(page: string): boolean {
        let pageAcl;
        let keys = Object.keys(PAGE_SECURITY_DEFS);

        for (let i = 0; i < keys.length; i++) {
            let key = keys[i];
            if (page.toLowerCase().indexOf(key.toLowerCase()) !== -1) {
                pageAcl = PAGE_SECURITY_DEFS[key];
                break;
            }
        }

        // If the page is in the ACL defined above, see if it passes, otherwise assume it's OK to navigate to the page
        return pageAcl ? pageAcl.some(acl => this.canAccess(acl).length === 0) : true;
    }

    can(securityDef: TSecurityDefinition, userId?: string): boolean {
        let definitionPermissions = DEFS[securityDef]();

        // If the definition is an array of arrays, treat as a OR, otherwise get the AND'd list of string permissions
        return definitionPermissions[0] instanceof Array ? definitionPermissions.some(acl => this.canAccess(acl, userId).length === 0) : this.canAccess(definitionPermissions, userId).length === 0;
    }

    getMissingClearances(securityDef: TSecurityDefinition, userId?: string): (string | string[])[] | never[] {
        let definitionPermissions: (string | string[])[] = DEFS[securityDef]();
        let missingClearances: (string | string[])[] = [];
        definitionPermissions.forEach(acl => {
            if (acl instanceof Array) {
                if (!acl.some(innerAcl => this.canAccess(innerAcl, userId).length === 0)) {
                    missingClearances.push(acl);
                }
            } else if (this.canAccess(acl, userId).length > 0) {
                missingClearances.push(acl);
            }
        });
        return missingClearances;
    }

    canAssignUnassignedLogsForWorkflow(): boolean {
        let currentUser = this.userService.getCurrentUser();
        return this.can("useHos") && !!currentUser && !!(currentUser.driverGroups || currentUser.keys) && this.store.getState().data.ELDJurisdiction !== "EWD";
    }

    canVerifyLogsForWorkflow(changeVehicle: boolean): boolean {
        return (this.can("useHos") || (this.can("attemptHos") && isNoVehicle())) && !changeVehicle && this.store.getState().data.ELDJurisdiction !== "EWD";
    }

    canUseDvirForWorkflow(): boolean {
        return this.can("useDvir") && !isNoVehicle();
    }

    private difference(arr: string[], others: string[]) {
        if (arr.length < 3) {
            return arr.reduce((result, item) => {
                if (others.indexOf(item) === -1) {
                    result.push(item);
                }

                return result;
            }, [] as string[]);
        } else {
            let hash = others.reduce((acc, item) => {
                acc[item] = true;
                return acc;
            }, <any>{});

            return arr.reduce((result, item) => {
                if (!hash.hasOwnProperty(item)) {
                    result.push(item);
                }

                return result;
            }, [] as string[]);
        }
    }

    private canAccess(controls: string | string[], userId?: string | null): string[] {

        let controlsArray: string[] = [].concat(<any>controls);
        let id = userId || this.userService.getCurrentUser()?.id;

        let acl: string[] = [];
        if (id) {
            acl = extend(true, [], this.store.getState().data.acl[id] || []);
        }
        let currentUser = this.userService.getCurrentUser();
        let isDriver = !!(currentUser && (currentUser.driverGroups || currentUser.keys));
        let currentUserId = currentUser && currentUser.id ? currentUser.id : undefined;
        let vehicleId = getCurrentVehicle() || {};
        let currentVehicle = this.deviceCache.get(vehicleId, currentUserId)[0] as Device || {};
        let deviceFlags = currentVehicle.deviceFlags || {};

        if (userId) {
            // these properties should be unique to the driver accessing if id is passed in
            let user: DeepReadonly<IUser> | null = this.userService.getUserById(userId, false);
            if (user) {
                isDriver = !!(user.driverGroups || user.keys);
            }
        }

        if (isDriver) {
            acl.push("User:IsDriver");
        }

        // If checking Device:UseHos, DevicePlan must be ProPlus or Pro
        if ((controlsArray.indexOf("Device:UseHos") !== -1) && deviceFlags.isHOSAllowed) {
            acl.push("Device:UseHos");
        }

        // Check feature flags and add to ACL if enabled
        controlsArray.forEach(control => {
            if (control.startsWith(FEATURE_FLAG_PREFIX)) {
                const flagName = control.substring(FEATURE_FLAG_PREFIX.length) as FeatureFlagName;
                if (this.featureFlagService.hasFeatureFlag(flagName)) {
                    acl.push(control);
                }
            }
        });

        return this.difference(controlsArray, acl);
    }

    private getSecurityIdentifierId(securityIdentifier: ISecurityIdentifier) {
        let securityId = securityIdentifier.securityId || {};

        // it's add-ins security identifiers
        if (securityId.customPageName) {
            // there are two types of add-in security identifiers:
            // 1. custom add-in identifiers (isSystemSecurityId = false). Its id is stored in "name" property
            // 2. identifier that hides/shows add-in (isSystemSecurityId = true). Its id is add-in name "customPageName"
            return (securityId.isSystemSecurityId ? securityId.customPageName : `${ securityId.customPageName }_${ securityId.name }`) || "";
        }

        return securityIdentifier.securityIdentifier;
    }

}
